<template>
  <div>
    <UILoadingState v-if="loading" class="h-full" />

    <BaseFetchStatusError v-else-if="error" :error="error" />

    <slot v-else />

    <div
      aria-live="assertive"
      class="z-20 pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
    >
      <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
        <transition-group
          enter-active-class="transform ease-out duration-300 transition"
          enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <UINotification
            v-for="notification in notifications.notifications"
            :key="notification.id"
            :headline="notification.headline"
            :body="notification.body"
            :level="notification.level"
            @close="notifications.remove(notification)"
          />
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useSite } from "~/stores/site.js";
import { UILoadingState, UINotification } from "@ultrafedt/components";
import { useNotifications } from "~/stores/notifications.js";
import { useSubmit } from "~/composables/useSubmit.js";
import BaseFetchStatusError from "~/components/common/BaseFetchStatusError.vue";
import { useAuth } from "~/stores/auth.js";

const auth = useAuth();
const site = useSite();
const notifications = useNotifications();
const { setLocale } = useI18n();

const siteRequest = useSubmit();
const profileRequest = useSubmit();

const loading = ref(true);
const error = ref(null);

onMounted(async () => {
  const siteResponse = await siteRequest.submit("/common/site", {
    method: "GET",
  });

  if (!siteResponse.ok) {
    error.value = siteResponse.error;

    return;
  }

  site.load(siteResponse.data.attributes);

  setLocale(siteResponse.data.attributes.locale);

  if (!auth.token || !site.customers) {
    auth.signOut();

    loading.value = false;

    return;
  }

  const profileResponse = await profileRequest.submit("/auth/profile", {
    method: "GET",
    quiet: true,
  });

  if (!profileResponse.ok) {
    auth.signOut();

    loading.value = false;

    return;
  }

  auth.setProfile(profileResponse.data.attributes);

  loading.value = false;
});

useHead({
  htmlAttrs: {
    class: "h-full bg-gray-50 notranslate",
    translate: "no",
  },
  bodyAttrs: {
    class: "h-full",
  },
  titleTemplate: computed(() => (site.name ? "%s | " + site.name : null)),
  link: computed(() => {
    const links = [];

    if (site.imageUrl) {
      links.push({
        rel: "icon",
        href: site.imageUrl,
      });
    }

    return links;
  }),
});
</script>
